import React, { useCallback, useMemo, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import i18next from 'i18next';
import { Prompt } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { shape, items, Success, Failure, ValidationError, Validator, fromModel, when } from '@totalsoft/pure-validations';
import { Box, Checkbox, FormControlLabel, Grid, IconButton, Chip, Tooltip, Typography } from '@mui/material';
import { Button } from '@totalsoft/rocket-ui';
import { BookmarkBorder, Check, CheckCircle, CreateOutlined, InfoOutlined, NewReleases, Update, Lock } from '@mui/icons-material';
import CascoQuotationHeaderComponent from './CascoQuotationHeaderComponent';
import CascoRisksComponent from './CascoRisksComponent';
import CascoCoefficientsComponent from './CascoCoefficientsComponent';
import { get, set, sequence, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { useMutation, useReactiveVar } from '@apollo/client';
import { SELECT_INSURANCE_QUOTATION, UPDATE_CASCO_QUOTATION } from '../../../mutations';
import { CASCO_QUOTATION_UPDATED } from '../../../subscriptions';
import { isDirty } from '@totalsoft/change-tracking';
import { useToast, useActivityIndicatorContext } from 'utils/hooks';
import { CASCO_QUOTATION_UPDATED_EVENT } from '../../../eventTypes';
import { useGlobalSubscription } from 'providers/GlobalCacheManager';
import { useServerErrorHandling } from 'utils/hooks/useServerErrorHandling';
import { insuranceConstants } from '../../../constants';
import { RightsContext } from 'providers/RightsContext';
import * as R from 'ramda';
import { env } from 'utils/env';
import { useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { cascoSelectedQuotationVar, cascoSelectionInProgressVar } from '../../../variables';
import useInsuranceParams from '../../../useInsuranceParams';
import { QuotationCard, QuotationButton } from '../../InsuranceStyles';

const validatorCoefficients = Validator(function isSelected(content) {
  return content.filter(item => item.selected).length !== 0
    ? Success
    : Failure(ValidationError(i18next.t('Validations.CascoQuotation.AtLeastOneDetail')));
});

const validator = cascoAllowEmptyAdditionalData => {
  return shape({
    coefficients: items(
      fromModel(coefficient =>
        shape({
          details: validatorCoefficients |> when(coefficient.allowMultipleDetails && !cascoAllowEmptyAdditionalData)
        })
      )
    )
  });
};

const CascoQuotationContainer = ({
  quotation,
  negotiationLens,
  ownerApplicationCode,
  isReadOnly,
  quotationIsDirty,
  isLastModifiedQuotation,
  setLastModifiedQuotationId
}) => {
  const [t] = useTranslation();
  const { onBeginCall, onEndCall } = useActivityIndicatorContext();
  const addToast = useToast();

  const { offerId, offerSiteId } = useInsuranceParams();
  const { isImage, cascoAllowEmptyAdditionalData } = useContext(RightsContext);
  const cascoSelectedQuotation = useReactiveVar(cascoSelectedQuotationVar);

  const [saving, setSaving] = useState(false);
  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(quotation);

  // #region Memos

  const cascoValidator = useMemo(() => validator(cascoAllowEmptyAdditionalData), [cascoAllowEmptyAdditionalData]);
  const [validation, validate] = useDirtyFieldValidation(cascoValidator);

  const readOnly = useMemo(() => {
    return (
      isReadOnly() ||
      (lens.sourceInsuranceQuotationId |> get) !== null ||
      (lens.cascoQuotation.conventionInfo?.isActive |> get) === false ||
      (lens.cascoQuotation.manualQuotation |> get) === true ||
      (lens.cascoQuotation.lockedYN |> get) === true
    );
  }, [isReadOnly, lens]);

  const isSelected = useMemo(() => quotation.id === cascoSelectedQuotation.id, [quotation, cascoSelectedQuotation]);

  const isLensDirty = useMemo(() => isDirty(dirtyInfo), [dirtyInfo]);

  const color = useMemo(() => {
    if (!quotation.sourceInsuranceQuotationId) return 'primary';
    return ownerApplicationCode === env.REACT_APP_LSNG_INSURANCE_APP_CODE ? 'info' : 'secondary';
  }, [ownerApplicationCode, quotation.sourceInsuranceQuotationId]);

  // #endregion

  useEffect(() => {
    quotationIsDirty.current = isLensDirty;
  }, [isLensDirty, quotationIsDirty]);

  useEffect(() => {
    resetLens(quotation);
  }, [quotation, resetLens]);

  const { storeKey } = useServerErrorHandling({
    effect: _ => onEndCall()
  });

  // #region Select quotation

  const [selectInsuranceQuotation] = useMutation(SELECT_INSURANCE_QUOTATION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
      onEndCall();
    },
    onCompleted: data => {
      const { correlationId } = data.selectInsuranceQuotation;
      storeKey({ correlationId });
    }
  });

  const handleSelectQuotation = useCallback(() => {
    onBeginCall();
    cascoSelectionInProgressVar(true);
    selectInsuranceQuotation({
      variables: {
        insuranceSimulationId: lens.insuranceSimulationId |> get,
        insuranceQuotationId: lens.id |> get
      }
    });
  }, [lens, selectInsuranceQuotation, onBeginCall]);

  // #endregion

  // #region Update quotation

  const [updateCascoQuotation] = useMutation(UPDATE_CASCO_QUOTATION, {
    context: {
      headers: { offerId, offerSiteId }
    },
    onError: err => {
      addToast(err.message, 'error');
      onEndCall();
      setSaving(false);
    },
    onCompleted: data => {
      const { correlationId } = data.updateCascoQuotation;
      storeKey({ correlationId });
    }
  });

  const handleSaveCascoQuotation = useCallback(() => {
    const cascoQuotation = {
      id: lens.id |> get,
      insuranceSimulationId: lens.insuranceSimulationId |> get,
      totalDamageDeductiblePercent: lens.cascoQuotation.totalDamageDeductiblePercent |> get,
      partialDamageDeductibleAmount: lens.cascoQuotation.partialDamageDeductibleAmount |> get,
      associatedRisks: lens.cascoQuotation.associatedRisks |> get,
      coefficients: lens.cascoQuotation.coefficients |> get
    };

    if (!validate(cascoQuotation)) return;

    onBeginCall();
    setSaving(true);
    setLastModifiedQuotationId(lens.id |> get);

    updateCascoQuotation({
      variables: {
        cascoQuotation
      }
    });
  }, [setLastModifiedQuotationId, updateCascoQuotation, lens, onBeginCall, validate]);

  const handleSelectQuotationForNegotiation = useCallback(
    (insuranceQuotationId, quotationName) => event => {
      const value = event.target.checked;
      const selectedQuotations = (negotiationLens.selectedQuotations |> get).filter(x => x.id !== insuranceQuotationId);
      if (value) {
        selectedQuotations.push({ id: insuranceQuotationId, name: quotationName });
      }
      (negotiationLens.selectedQuotations |> set)(selectedQuotations);
    },
    [negotiationLens]
  );

  const handleCoefficientsSelectionChange = useCallback(
    (selectedCoefficientDetails, additionalDataTypeId) => {
      (lens.cascoQuotation.coefficients |> sequence).forEach(
        coefficient =>
          (coefficient.additionalDataTypeId |> get) === additionalDataTypeId &&
          (coefficient.details |> sequence).forEach(detail =>
            coefficient.allowMultipleDetails |> get
              ? selectedCoefficientDetails.includes(detail.detailId |> get)
                ? (detail.selected |> set)(true)
                : (detail.selected |> set)(false)
              : (detail.detailId |> get) === selectedCoefficientDetails
              ? (detail.selected |> set)(true)
              : (detail.selected |> set)(false)
          )
      );
    },
    [lens.cascoQuotation.coefficients]
  );

  const handleDeleteCoefficientsSelection = useCallback(
    (additionalDataTypeId, detailId) => {
      (lens.cascoQuotation.coefficients |> sequence).forEach(coefficient => {
        (coefficient.additionalDataTypeId |> get) === additionalDataTypeId &&
          (coefficient.details |> sequence).forEach(detail => (detail.detailId |> get) === detailId && (detail.selected |> set)(false));
      });
    },
    [lens]
  );

  // #endregion

  // #region Subscriptions

  useGlobalSubscription(CASCO_QUOTATION_UPDATED, {
    variables: { cascoQuotationId: lens.id |> get },
    onSubscriptionData: ({
      subscriptionData: {
        data: { cascoQuotationUpdated }
      }
    }) => {
      setSaving(false);

      if (!cascoQuotationUpdated) return;

      const currentQuotation = lens |> get;
      resetLens({
        ...currentQuotation,
        insurerName: cascoQuotationUpdated.quotation.insurerName,
        period: cascoQuotationUpdated.quotation.period,
        cascoQuotation: {
          ...currentQuotation.cascoQuotation,
          insuranceAmount: cascoQuotationUpdated.quotation.cascoQuotation.insuranceAmount,
          totalInsuranceAmount: cascoQuotationUpdated.quotation.cascoQuotation.totalInsuranceAmount,
          totalDamageDeductiblePercent: cascoQuotationUpdated.quotation.cascoQuotation.totalDamageDeductiblePercent,
          partialDamageDeductibleAmount: cascoQuotationUpdated.quotation.cascoQuotation.partialDamageDeductibleAmount,
          periodicity: cascoQuotationUpdated.quotation.cascoQuotation.periodicity,
          insuredAmount: cascoQuotationUpdated.quotation.cascoQuotation.insuredAmount,
          associatedRisks: cascoQuotationUpdated.quotation.cascoQuotation.associatedRisks,
          coefficients: cascoQuotationUpdated.quotation.cascoQuotation.coefficients
        }
      });
      if (!cascoQuotationUpdated.silentMode) {
        addToast(t('Notifications.' + CASCO_QUOTATION_UPDATED_EVENT), 'info');
      }
      onEndCall();

      setTimeout(() => {
        setLastModifiedQuotationId(null);
      }, insuranceConstants.showModifiedQuotationMs);
    }
  });

  // #endregion

  return (
    <Grid container>
      <Prompt when={isLensDirty} message={() => t('SaveChanges', { card: t('Offer.Insurance.CASCO') })} />
      <QuotationCard
        icon={
          quotation.sourceInsuranceQuotationId
            ? ownerApplicationCode === env.REACT_APP_LSNG_INSURANCE_APP_CODE
              ? Update
              : Check
            : quotation.cascoQuotation?.manualQuotation === true
            ? CreateOutlined
            : BookmarkBorder
        }
        title={
          quotation.sourceInsuranceQuotationId ? (
            ownerApplicationCode === env.REACT_APP_LSNG_INSURANCE_APP_CODE ? (
              <span>{t('Offer.Insurance.OfferInNegotiation')}</span>
            ) : (
              <span>{t('Offer.Insurance.OfferNegotiated')}</span>
            )
          ) : (
            <></>
          )
        }
        iconColor={color}
        selected={isSelected}
        disabled={
          (lens.cascoQuotation?.conventionInfo?.isActive |> get) === false && (lens.cascoQuotation.manualQuotation |> get) === false
        }
        isLastModified={isLastModifiedQuotation}
      >
        <Grid container spacing={3}>
          <Grid item container xs={12} justifyContent='center' alignItems='center' spacing={1}>
            <Grid item>
              <Typography variant='subtitle1'>
                <Box fontWeight={'fontWeightBold'}>
                  {lens.insurerName |> get}
                  {isLastModifiedQuotation && (
                    <Tooltip title={t('Offer.Insurance.LastQuotationModified')}>
                      <InfoOutlined fontSize='small' />
                    </Tooltip>
                  )}
                  {(lens.cascoQuotation?.manualQuotation |> get) === true && (
                    <Tooltip title={t('Offer.Insurance.ManualQuotation')}>
                      <NewReleases />
                    </Tooltip>
                  )}
                </Box>
              </Typography>
            </Grid>
            {(negotiationLens.negotiationChecked |> get) && (
              <Grid item>
                <Tooltip title={t('Offer.Insurance.SelectQuotationForNegotiation')}>
                  <FormControlLabel
                    disabled={isSelected}
                    control={
                      <Checkbox
                        checked={R.any(x => x.id === quotation.id)(negotiationLens.selectedQuotations |> get)}
                        id={'quotationNegotiation_check'}
                        color={'primary'}
                        onChange={handleSelectQuotationForNegotiation(
                          quotation.id,
                          quotation.cascoQuotation?.conventionInfo?.conventionName
                        )}
                      />
                    }
                    onClick={event => event.stopPropagation()}
                    onFocus={event => event.stopPropagation()}
                  />
                </Tooltip>
              </Grid>
            )}
            {(lens.cascoQuotation?.lockedYN |> get) === true && (
              <Grid item xs={1}>
                <Tooltip title={t('Offer.Insurance.QuotationLocked')}>
                  <span>
                    <IconButton id={`quotation_lock_${lens.cascoQuotation.Id |> get}`} disabled>
                      <Lock />
                    </IconButton>
                  </span>
                </Tooltip>
              </Grid>
            )}
          </Grid>
          <Grid item xs={12}>
            <CascoQuotationHeaderComponent lens={lens} disabled={readOnly} />
          </Grid>
          <Grid item xs={12}>
            <CascoRisksComponent lens={lens} color={color} disabled={readOnly} />
          </Grid>
          <Grid item xs={12}>
            <CascoCoefficientsComponent
              lens={lens}
              color={color}
              disabled={readOnly}
              onCoefficientsSelectionChange={handleCoefficientsSelectionChange}
              onDeleteCoefficientsSelection={handleDeleteCoefficientsSelection}
              cascoAllowEmptyAdditionalData={cascoAllowEmptyAdditionalData}
              validation={validation}
            />
          </Grid>
          <Grid item container spacing={2} xs={12} justifyContent='center'>
            {!isImage && (
              <Grid item xs>
                <Button
                  id={'cascoComparator_saveBtn'}
                  color='primary'
                  onClick={handleSaveCascoQuotation}
                  fullWidth
                  disabled={saving || !isDirty(dirtyInfo) || readOnly}
                >
                  {saving ? t('Saving') : t('Save')}
                </Button>
              </Grid>
            )}
            <Grid item xs>
              <QuotationButton
                id={'cascoComparator_selectBtn'}
                fullWidth
                onClick={handleSelectQuotation}
                disabled={
                  isSelected ||
                  !(lens.cascoQuotation.insuranceAmount |> get) ||
                  (lens.cascoQuotation.insuranceAmount |> get) === 0 ||
                  (lens.cascoQuotation.lockedYN |> get) === true ||
                  isDirty(dirtyInfo) ||
                  ((lens.cascoQuotation?.conventionInfo?.isActive |> get) === false &&
                    (lens.cascoQuotation.manualQuotation |> get) === false) ||
                  isReadOnly()
                }
                endIcon={isSelected && <CheckCircle />}
                quotationIsSelected={isSelected}
              >
                {isSelected ? t('Selected') : t('Select')}
              </QuotationButton>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption'>
              <Box>{lens.freeTxt |> get}</Box>
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='caption'>
              <Box>
                {t('Offer.Insurance.LastEdit', {
                  date: t('DATE_FORMAT', {
                    date: {
                      value: lens.cascoQuotation.quotationDate |> get,
                      format: 'DD.MM.YYYY HH:mm:ss'
                    }
                  })
                })}
              </Box>
            </Typography>
          </Grid>
          {(lens.cascoQuotation?.manualQuotation |> get) === false && (lens.cascoQuotation?.conventionInfo?.isActive |> get) === false && (
            <Grid item xs={12} alignContent='flex-start' justifyContent='flex-start'>
              <Chip label={t('Offer.Insurance.UnavailableOffer')} />
            </Grid>
          )}
        </Grid>
      </QuotationCard>
    </Grid>
  );
};

CascoQuotationContainer.propTypes = {
  quotation: PropTypes.object.isRequired,
  negotiationLens: PropTypes.object.isRequired,
  ownerApplicationCode: PropTypes.string,
  isReadOnly: PropTypes.func.isRequired,
  quotationIsDirty: PropTypes.object.isRequired,
  isLastModifiedQuotation: PropTypes.bool.isRequired,
  setLastModifiedQuotationId: PropTypes.func.isRequired
};

export default CascoQuotationContainer;
